// @flow

import { Component, Fragment } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { Redirect } from 'react-router-dom';
import { Navbar, NavbarGroup } from '@blueprintjs/core';

import { getAuth0Client, isBeingImpersonated } from '../../../services/auth0';
import * as ROUTES from '../../../constants/routes';
import Layout from '../../layouts/default-logged-in';
import DefaultLoggedIn from '../../layouts/default-logged-in';
import TopBarLoading from '../../top-bar-loading';
import NewUserSplashPage from '../new-user-splash';
import * as LocationModel from '../../../models/location';
import * as RecordingModel from '../../../models/recording';
import * as UserModel from '../../../models/user';
import * as ReportModel from '../../../models/report';
import { getTheme } from '../../../styles/theme';
import PreInitLoading from '../pre-init-loading';
import { initAPIConnection } from '../../../services/sql-api';
import { dropTables } from '../../../models/database';

const TopBar = styled(Navbar)`
  padding-left: 2em;
  padding-right: 2em;
`;

// override any blueprint styles here with theme variables
const BlueprintOverrides = styled.div`
  font-family: var(--fonts-default);

  .bp3-card {
    border-radius: var(--widget-border-radius);
  }

  .bp3-button:not([class*='bp3-intent-']) .bp3-icon {
    color: var(--button-icon-color);
  }

  .bp3-tab {
    outline: none;
  }

  .bp3-tab[aria-selected='true'] {
    color: var(--selected-text);
  }

  .bp3-tab-indicator-wrapper .bp3-tab-indicator {
    background-color: var(--selected-text);
  }

  .bp3-tag.bp3-intent-success {
    background-color: var(--ok);
  }

  .bp3-tag.bp3-intent-danger {
    background-color: var(--error);
  }
`;

const Wrapper = styled.div`
  background: var(--main-background);
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

// Auth0 getTokenSilently() `.error` codes that mean the session / refresh token
// is genuinely gone and only an interactive login can recover it. We
// deliberately EXCLUDE transient or environmental failures so we never bounce a
// user whose session is actually fine:
//   - `timeout` (TimeoutError): flaky network OR a third-party-cookie-blocked
//     silent-auth iframe — not necessarily unrecoverable.
//   - generic network / "failed to fetch": transient.
//   - `mfa_required`: a different (non-stale-token) flow.
//   - `access_denied`: an Auth0 Action/Rule is actively denying — a re-login
//     would only loop.
// Note: with useRefreshTokens:true the primary renewal is a first-party
// POST /oauth/token (not cookie-dependent); the cookie-sensitive iframe is only
// a fallback when the refresh token is missing/invalid.
const REAUTH_REQUIRED_ERRORS = [
  'login_required',
  'invalid_grant',
  'missing_refresh_token',
  'interaction_required',
  'consent_required',
];

const requiresInteractiveReauth = (error: ?{ error?: string }): boolean =>
  !!error && REAUTH_REQUIRED_ERRORS.includes(error.error);

// Guards against a logout/login loop: if getTokenSilently() keeps failing even
// after a fresh login (e.g. a genuinely broken config), we only auto-recover
// once per tab session. Cleared as soon as a token is successfully minted.
const STALE_SESSION_KEY = 'auraStaleAuth0SessionRecovered';

type Props = {
  refreshUser: () => void,
  logout: ({ returnTo?: string }) => void,
  userRefreshCompleted: boolean,
  userRefreshAuthFailure: boolean,
  isUserNewlyCreated: boolean,
  children: any,
  user: UserModel.t,
  location: any, // BrowserHistory location
  recordings: Array<RecordingModel.t>,
  locations: Array<LocationModel.t>,
  fetchRecordings: () => void,
  fetchingData: boolean,
  reports: Array<ReportModel.t>,
  fetchReports: () => void,
  fetchChatThreads: () => void,
};

type State = {
  auth0CheckCompleted: boolean,
  isAuthenticated: boolean,
  theme: Object,
  isReady: boolean,
  dataFetchStarted: boolean,
};

class Authenticate extends Component<Props, State> {
  state = {
    auth0CheckCompleted: false,
    isAuthenticated: false,
    theme: {},
    isReady: false,
    dataFetchStarted: false,
  };

  async componentDidMount() {
    const { recordings, locations, reports, fetchReports, fetchRecordings, fetchChatThreads } =
      this.props;

    const getData = async () => {
      if (!!recordings.length && !!locations.length && !!reports.length) {
        // skip fetching anything if we already have data
        this.setState({ isReady: true });
      } else {
        if (!recordings.length || !locations.length)
          fetchRecordings();
        if (!reports.length) fetchReports();
        fetchChatThreads();
        this.setState({ dataFetchStarted: true });
      }
    };
      const auth0Client = getAuth0Client();

      if (auth0Client) {
        const isAuthenticated = await auth0Client.isAuthenticated();
        this.setState({ auth0CheckCompleted: true, isAuthenticated });

        if (isAuthenticated) {
          // `isAuthenticated()` only checks the cached id-token in localStorage,
          // so a user whose refresh token has been revoked or has expired still
          // passes it — but then every API call throws because
          // getTokenSilently() can no longer mint an access token, leaving them
          // stuck on the "Authentication failure" screen. Proactively verify we
          // can still get a token; if renewal fails specifically because the
          // session needs an interactive re-auth, clear the stale Auth0 cache
          // and force a clean re-login. Any other failure (network, timeout,
          // cookie policy, mfa, policy denial) is left to the existing flow so
          // we never bounce a user whose session is actually recoverable.
          // Guarded by a session flag so we auto-recover at most once per tab.
          try {
            await auth0Client.getTokenSilently();
            window.sessionStorage.removeItem(STALE_SESSION_KEY);
          } catch (tokenError) {
            if (
              requiresInteractiveReauth(tokenError) &&
              !window.sessionStorage.getItem(STALE_SESSION_KEY)
            ) {
              window.sessionStorage.setItem(STALE_SESSION_KEY, '1');
              // eslint-disable-next-line no-console
              console.warn(
                'Stale Auth0 session detected; clearing tokens and re-authenticating',
                tokenError,
              );
              this.props.logout({ returnTo: window.location.origin });
              return;
            }
            // eslint-disable-next-line no-console
            console.error(
              'getTokenSilently failed at load; not forcing logout',
              tokenError,
            );
          }

          const isImpersonating = await isBeingImpersonated();

          // delete the local cache when switching user
          if (isImpersonating) {
            console.log('This user is impersonating another user, so clearing the API cache');
            await dropTables();
          }

          // start the websocket as soon as we're authenticated
          initAPIConnection().then(() => getData());
          await this.props.refreshUser();
        }

        this.setState({ theme: await getTheme() });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { fetchingData } = this.props;
    const { dataFetchStarted, isReady } = this.state;

    if (dataFetchStarted && !isReady && !fetchingData) {
      this.setState({ isReady: true });
    }
  }

  render() {
    const {
      userRefreshCompleted,
      userRefreshAuthFailure,
      isUserNewlyCreated,
      children,
      location,
      refreshUser,
      user,
    } = this.props;

    if (this.state.theme !== null && this.state.theme.properties) {
      const body = document.querySelector('body');
      Object.entries(this.state.theme.properties).forEach(
        ([property, value]: [string, mixed]) => {
          if (body && body.style && typeof value === 'string') {
            body.style.setProperty(property, value);
          }
        },
      );
    }

    const { auth0CheckCompleted, isAuthenticated } = this.state;

    if (auth0CheckCompleted && !isAuthenticated)
      return (
        <Redirect to={{ pathname: ROUTES.LOGIN, state: { from: location }, search: window.location.search }} />
      );

    if (userRefreshAuthFailure)
      return (
        <Layout>
          <TopBar>
            <NavbarGroup>Authentication failure</NavbarGroup>
          </TopBar>
        </Layout>
      );

    if (auth0CheckCompleted && isAuthenticated && userRefreshCompleted) {
      if (isUserNewlyCreated)
        return <NewUserSplashPage user={user} refreshUser={refreshUser} />;
      return (
        <>
          {this.state.theme && Object.keys(this.state.theme).length ? (
            <ThemeProvider theme={this.state.theme}>
              <BlueprintOverrides>
                <Wrapper>
                  <Fragment>
                    {this.state.isReady ? (
                      children
                    ) : (
                      <DefaultLoggedIn>
                        <TopBarLoading />
                      </DefaultLoggedIn>
                    )}
                  </Fragment>
                </Wrapper>
              </BlueprintOverrides>
            </ThemeProvider>
          ) : (
            <PreInitLoading />
          )}
        </>
      );
    }

    // user refresh is still in progress....
    return (
      <>
        {this.state.theme && Object.keys(this.state.theme).length ? (
          <ThemeProvider theme={this.state.theme}>
            <BlueprintOverrides>
              <Layout>
                <TopBar>
                  <NavbarGroup>
                    <TopBarLoading />
                  </NavbarGroup>
                </TopBar>
              </Layout>
            </BlueprintOverrides>
          </ThemeProvider>
        ) : (
          <PreInitLoading />
        )}
      </>
    );
  }
}

export default Authenticate;
